import * as React from 'react';
import { View, Text, Image } from '@react-pdf/renderer';
import PDFDocument, { PDFConfig } from '../base/PDFDocument';
import PDFPatientInfo from '../base/PDFPatientInfo';
import PDFBillItemsTable from '../base/PDFBillItemsTable';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles, specializedTypography } from '../styles/typography';
import { formatDateForPDF } from '../utils/dataFormatters';
import { createT } from '../utils/simpleTranslate';
import { Bill, DocumentType, DocumentStatus, PaymentRecord } from '../../../pages/hospital/Billing/types';
import { documentTypeToString } from '../../../utils/bill';
import { calculateTotalBill, redistributeHiddenValue } from '../../../utils/bill';
import { billPdfLayoutForDocumentType } from '../styles/billPdfLayouts';
import { TYPE_BILL_ITEM } from '../../../constants/types';

// Import signature images
import SignatureMajor from '../../../img/signatures/CHOM-major.png';
import SignatureFinancier from '../../../img/signatures/CHOM-financier.png';
import SignatureDirecteur from '../../../img/signatures/CHOM-directeur.jpeg';

/**
 * Bill PDF Document Component
 * 
 * Renders bills in PDF format for three document types:
 * - BUDGET: Estimated costs with insurance information
 * - INVOICE: Final bill with insurance details and payment info
 * - SUMMARY: Detailed breakdown with costs and payment history
 */

interface IPatient {
  personalData: {
    name?: string;
    surnames?: string;
    birthdate?: string | Date;
    sex?: string;
    phone?: string;
    healthId?: string;
    nationalId?: string;
    passportNumber?: string;
    insurance?: string | number;
  };
}

interface IResearcher {
  uuid: string;
  name: string;
  surnames: string;
}

interface IDepartment {
  uuid?: string;
  name: string;
}

interface InsuranceClaim {
  organizationName?: string | null;
  souscripteur?: string | null;
  requestedAmount?: number | null;
  interventionDate?: string | null;
  guaranteeLetterIssueDate?: string | null;
  guaranteeLetterNumber?: string | null;
  amountInLetters?: string | null;
}

/**
 * Build this document's `pec` prop from a claim.
 *
 * SHER-713: two callers (BillView and the Billing page's download action) each
 * hand-wrote this same field-by-field conversion, and adding `souscripteur`
 * updated only one of them, so the field reached one invoice PDF and not the
 * other. The shape belongs to this component, so the conversion does too —
 * a new field is now added in one place or in none.
 */
export const toPecForPDF = (
  claim: {
    organizationName?: string | null;
    souscripteur?: string | null;
    requestedAmount?: number | null;
    interventionDate?: Date | string | null;
    guaranteeLetterIssueDate?: Date | string | null;
    guaranteeLetterNumber?: string | null;
    amountInLetters?: string | null;
  } | null | undefined,
): InsuranceClaim => {
  const asIsoString = (value: Date | string | null | undefined): string | null =>
    value ? (value instanceof Date ? value.toISOString() : String(value)) : null;

  return {
    organizationName: claim?.organizationName || null,
    souscripteur: claim?.souscripteur || null,
    requestedAmount: claim?.requestedAmount || null,
    interventionDate: asIsoString(claim?.interventionDate),
    guaranteeLetterIssueDate: asIsoString(claim?.guaranteeLetterIssueDate),
    guaranteeLetterNumber: claim?.guaranteeLetterNumber || null,
    amountInLetters: claim?.amountInLetters || null,
  };
};

interface BillPDFDocumentProps {
  bill: Bill;
  uuidInvestigation: string;
  type: DocumentType;
  uuidPrescribingDoctor: string | null;
  uuidDepartment: string | null;
  currency: string;
  patient: IPatient;
  locale?: string;
  config: PDFConfig;
  paymentRecords?: PaymentRecord[];
  pec: InsuranceClaim;
  prescribingDoctor?: IResearcher;
  department?: IDepartment;
  patientInsurance?: { name: string } | null;
  patientCoverage?: { name: string } | null;
}

const formatCurrency = (amount: number, currency: string, locale: string): string => {
  // Replace non-breaking spaces and other special space characters with regular spaces
  const formatted = new Intl.NumberFormat(locale).format(amount).replace(/[\u00A0\u202F\u2009]/g, ' ');
  return `${formatted} ${currency}`;
};

const formatResearcherName = (researcher: IResearcher): string => {
  return `${researcher.name} ${researcher.surnames}`.trim();
};

export const BillPDFDocument: React.FC<BillPDFDocumentProps> = ({
  bill,
  uuidInvestigation,
  type,
  currency,
  patient,
  locale = 'fr',
  config,
  paymentRecords,
  pec,
  prescribingDoctor,
  department,
  patientInsurance,
  patientCoverage,
}) => {
  const t = createT(locale);

  // SHER-335: redistribute the HIDDEN_VALUE amount into the visible acts so every total
  // below (grand total, table total, patient share) includes it while the line stays hidden.
  const distributedItems = React.useMemo(() => redistributeHiddenValue(bill.billItems), [bill.billItems]);

  // Calculate totals
  const totalBill = React.useMemo(() => {
    return calculateTotalBill(distributedItems, [TYPE_BILL_ITEM.HIDDEN_VALUE, TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO]);
  }, [distributedItems]);

  const totalPaidFromRecords = React.useMemo(() => {
    return paymentRecords ? paymentRecords.reduce((sum, record) => sum + record.amount, 0) : 0;
  }, [paymentRecords]);

  const insuranceAmount = pec?.requestedAmount || 0;
  const remainingAmount = totalBill - totalPaidFromRecords - insuranceAmount;
  const layout = billPdfLayoutForDocumentType(type);
  const billFormTitleStyle = layout.billFormTitle.style === 'body2' ? typographyStyles.body2 : typographyStyles.h4;

  // Render bill details (right side) - similar to renderRequestDetails in RequestPDFDocument
  const renderBillDetails = (width = '60%'): React.ReactNode => (
    <View style={{ flexDirection: 'row', width }}>
      <View style={{ width: '100%', paddingLeft: 2 }}>
        {/* Invoice/Bill Number */}
        <View style={{ marginBottom: 1 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {type === DocumentType.INVOICE
                ? t('hospital.billing.pdf.invoice.number')
                : type === DocumentType.BUDGET
                ? t('hospital.billing.bill.num_bill')
                : t('hospital.billing.pdf.document_number')
              }:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{bill.documentNumber}
            </Text>
          </Text>
        </View>

        {/* SUMMARY: bill id + creation date in the right column */}
        {type === DocumentType.SUMMARY && bill.createdAt && (
          <View style={{ marginBottom: 1 }}>
            <Text>
              <Text style={specializedTypography.fieldLabel}>
                {t('hospital.billing.pdf.date_created')}:
              </Text>
              <Text style={specializedTypography.fieldValue}>
                {' '}{formatDateForPDF(bill.createdAt, locale)}
              </Text>
            </Text>
          </View>
        )}

        {/* Insurance Information for INVOICE */}
        {(type === DocumentType.BUDGET || type === DocumentType.INVOICE) && pec?.organizationName && (
          <>
            <View style={{ marginBottom: 1 }}>
              <Text>
                <Text style={specializedTypography.fieldLabel}>
                  {t('hospital.billing.pdf.invoice.attention')}:
                </Text>
                <Text style={specializedTypography.fieldValue}>
                  {' '}{pec.organizationName}
                </Text>
              </Text>
            </View>

            {pec.souscripteur && (
              <View style={{ marginBottom: 1 }}>
                <Text>
                  <Text style={specializedTypography.fieldLabel}>
                    {t('hospital.billing.insurance_claims.form.souscripteur')}:
                  </Text>
                  <Text style={specializedTypography.fieldValue}>
                    {' '}{pec.souscripteur}
                  </Text>
                </Text>
              </View>
            )}

            {pec.guaranteeLetterNumber && (
              <View style={{ marginBottom: 1 }}>
                <Text>
                  <Text style={specializedTypography.fieldLabel}>
                    {t('hospital.billing.pdf.invoice.police_number')}:
                  </Text>
                  <Text style={specializedTypography.fieldValue}>
                    {' '}{pec.guaranteeLetterNumber}
                  </Text>
                </Text>
              </View>
            )}

            {pec.guaranteeLetterIssueDate && (
              <View style={{ marginBottom: 1 }}>
                <Text>
                  <Text style={specializedTypography.fieldLabel}>
                    {t('hospital.billing.pdf.invoice.date_emission')}:
                  </Text>
                  <Text style={specializedTypography.fieldValue}>
                    {' '}{formatDateForPDF(pec.guaranteeLetterIssueDate, locale)}
                  </Text>
                </Text>
              </View>
            )}

            {pec.interventionDate && (
              <View style={{ marginBottom: 1 }}>
                <Text>
                  <Text style={specializedTypography.fieldLabel}>
                    {t('hospital.billing.pdf.invoice.date_surgery')}:
                  </Text>
                  <Text style={specializedTypography.fieldValue}>
                    {' '}{formatDateForPDF(pec.interventionDate, locale)}
                  </Text>
                </Text>
              </View>
            )}
          </>
        )}

        {/* Budget information */}
        {type === DocumentType.BUDGET && pec?.organizationName && (
          <View style={{ marginBottom: 1 }}>
            <Text>
              <Text style={specializedTypography.fieldLabel}>
                {t('hospital.billing.pdf.invoice.attention')}:
              </Text>
              <Text style={specializedTypography.fieldValue}>
                {' '}{pec.organizationName}
              </Text>
            </Text>
          </View>
        )}
      </View>
    </View>
  );

  // Combine patient info and bill details in one row (like RequestPDFDocument)
  // SHER-425 follow-up: the Synthèse (SUMMARY) header was ~6 patient items in a narrow
  // left column vs 2 on the right — tall and unbalanced. Render patient info in two
  // columns and widen it so the block is ~3 rows instead of 6 (Pedro, header rebalance).
  const renderPatientAndBillInfo = (): React.ReactNode => {
    const isSummary = type === DocumentType.SUMMARY;
    return (
      <View style={{ flexDirection: 'row', width: '100%' }}>
        <View style={{ width: isSummary ? '58%' : '40%' }}>
          <PDFPatientInfo
            patient={patient}
            locale={locale}
            patientCoverage={patientCoverage}
            columns={isSummary ? 2 : 1}
          />
        </View>
        {renderBillDetails(isSummary ? '42%' : '60%')}
      </View>
    );
  };

  // SHER-416: stamp the document status onto the Synthèse des actes (SUMMARY) PDF. It can now
  // be exported while still Modifiable (DRAFT); the banner makes clear a provisional export is
  // not a final/definitive document. A CLOSED summary gets a discreet "Non modifiable" line.
  // Scoped to SUMMARY only — budgets/invoices are still gated to CLOSED and keep their existing
  // PDF output unchanged.
  const renderStatusBanner = (): React.ReactNode => {
    if (type !== DocumentType.SUMMARY) {
      return null;
    }
    if (bill.status === DocumentStatus.DRAFT) {
      return (
        <View
          style={{
            borderWidth: 1,
            borderColor: '#b71c1c',
            backgroundColor: '#fdecea',
            paddingVertical: layout.statusBanner.paddingVertical,
            paddingHorizontal: 8,
            marginBottom: layout.statusBanner.marginBottom,
          }}
        >
          <Text style={[typographyStyles.body2, { color: '#b71c1c', fontWeight: 'bold', textAlign: 'center' }]}>
            {t('hospital.billing.pdf.status_provisional')}
          </Text>
        </View>
      );
    }
    return (
      <View style={{ marginBottom: 6 }}>
        <Text style={specializedTypography.fieldLabel}>
          {t('hospital.billing.pdf.status_label')}:{' '}
          <Text style={specializedTypography.fieldValue}>{t('hospital.billing.pdf.status_final')}</Text>
        </Text>
      </View>
    );
  };

  // Render title based on document type
  const renderTitle = (): React.ReactNode => (
    <Text style={typographyStyles.h2}>
      {t(`hospital.billing.pdf.title.${documentTypeToString(type)}`)}
    </Text>
  );

  // Render subtitle based on document type (simplified - details now in header)
  const renderSubtitle = (): React.ReactNode => {
    switch (type) {
      case DocumentType.SUMMARY:
        return (
          <View style={{ marginBottom: layout.subtitleMarginBottom }}>
            <Text style={[typographyStyles.body2, { textAlign: 'left' }]}>
              {t(`hospital.billing.pdf.subtitle.${documentTypeToString(type)}`)}
            </Text>
          </View>
        );
      
      case DocumentType.BUDGET:
      case DocumentType.INVOICE:
      default:
        return null;
    }
  };

  // Render prelude/body content based on document type
  const renderPrelude = (): React.ReactNode => {
    switch (type) {
      case DocumentType.SUMMARY: {
        const paidExternally = insuranceAmount || 0;
        const paid = totalPaidFromRecords;
        const toPay = totalBill - paidExternally - paid;

        return (
          <View style={{ marginBottom: layout.prelude.marginBottom }} wrap={layout.prelude.wrap}>
            {/* SHER-425 follow-up: "Bilan de l'acte" heading removed — redundant now that
                "Détails de l'acte" below carries the financial summary (Pedro, 2026-07-07). */}
            <View style={{ flexDirection: 'row', width: '100%', marginBottom: 4 }}>
              <View style={{ width: '50%', paddingRight: 8 }}>
                {prescribingDoctor && (
                  <Text style={typographyStyles.body2}>
                    <Text style={{ fontWeight: 'bold' }}>{t('hospital.billing.pdf.doctor')}</Text>: {formatResearcherName(prescribingDoctor)}
                  </Text>
                )}
                {department && (
                  <Text style={typographyStyles.body2}>
                    <Text style={{ fontWeight: 'bold' }}>{t('hospital.billing.pdf.department')}</Text>: {department.name}
                  </Text>
                )}
              </View>
            </View>

            <Text style={[typographyStyles.body2, { fontWeight: 'bold', marginTop: 2 }]}>
              {t('hospital.billing.pdf.detail')}
            </Text>
            <View style={{ borderBottomWidth: 1, borderBottomColor: '#000', marginVertical: 2, width: '100%' }} />

            <Text style={[typographyStyles.body2, { flexWrap: 'wrap' }]}>
              <Text style={{ fontWeight: 'bold' }}>{t('hospital.billing.pdf.total_cost')}</Text>: {formatCurrency(totalBill, currency, locale)}
              {'  |  '}
              <Text style={{ fontWeight: 'bold' }}>{t('hospital.billing.pdf.paid_externally')}</Text>: {formatCurrency(paidExternally, currency, locale)}
              {'  |  '}
              <Text style={{ fontWeight: 'bold' }}>{t('hospital.billing.pdf.paid')}</Text>: {formatCurrency(paid, currency, locale)}
              {'  |  '}
              <Text style={{ fontWeight: 'bold' }}>{t('hospital.billing.pdf.to_pay')}</Text>: {formatCurrency(toPay, currency, locale)}
            </Text>
          </View>
        );
      }
      
      case DocumentType.BUDGET:
      case DocumentType.INVOICE:
      default:
        return null;
    }
  };

  // Render payment summary table
  const renderPaymentSummaryTable = (): React.ReactNode => {
    // SUMMARY already shows paid / to-pay in the prelude block (SHER-425).
    if (type === DocumentType.SUMMARY) {
      return null;
    }
    const hasPayments = !!paymentRecords && paymentRecords.length > 0;
    const showInsuranceBreakdown = !!(pec?.organizationName && insuranceAmount);
    // SHER-316: the insurance/payment breakdown must appear on any insured invoice,
    // even before the patient has paid their remaining share. Only the payment-history
    // table (total paid / remaining) is gated on payments actually existing.
    if (!hasPayments && !showInsuranceBreakdown) {
      return null;
    }
    const paidPatient = insuranceAmount
    ? calculateTotalBill(distributedItems, [TYPE_BILL_ITEM.HIDDEN_VALUE]) - insuranceAmount
    : 0;
    return (
      <View wrap={false}>
        {showInsuranceBreakdown && (
              <View>
                <Text style={typographyStyles.body2}>
                  {t('hospital.billing.pdf.amount_paid_insurance')} <Text style={{ fontWeight: 'bold' }}>{pec.organizationName}</Text>: {formatCurrency(insuranceAmount, currency, locale)}
                </Text>
                <Text style={typographyStyles.body2}>
                  {t('hospital.billing.pdf.amount_paid_patient')}: {formatCurrency(paidPatient, currency, locale)}
                </Text>
                <Text style={typographyStyles.body2}>
                  {t('hospital.billing.pdf.total_letters')}: {pec.amountInLetters || formatCurrency(paidPatient, currency, locale)}
                </Text>
                <View style={{ borderBottomWidth: 1, borderBottomColor: '#000', marginTop: 5, marginBottom: 5, width: '100%' }} />
              </View>
            )}
        {hasPayments && (
          <>
            <Text style={[typographyStyles.h6, { fontWeight: 'bold', marginBottom: 5 }]}>
              {t('pages.hospital.billing.pdf.payment_summary')}
            </Text>

            <View style={{ backgroundColor: '#f9f9f9', padding: 5, borderTopWidth: 1, borderTopColor: '#ddd' }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={[typographyStyles.body2, { fontWeight: 'bold' }]}>
                  {t('pages.hospital.billing.pdf.total_paid')}
                </Text>
                <Text style={[typographyStyles.body2, { fontWeight: 'bold' }]}>
                  {formatCurrency(totalPaidFromRecords, currency, locale)}
                </Text>
              </View>
            </View>

            <View style={{ backgroundColor: '#e8f5e8', padding: 5, borderTopWidth: 1, borderTopColor: '#ddd' }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={[typographyStyles.body2, { fontWeight: 'bold', color: remainingAmount > 0 ? '#d32f2f' : '#2e7d32' }]}>
                  {t('pages.hospital.billing.pdf.remaining_amount')}
                </Text>
                <Text style={[typographyStyles.body2, { fontWeight: 'bold', color: remainingAmount > 0 ? '#d32f2f' : '#2e7d32' }]}>
                  {formatCurrency(Math.abs(remainingAmount), currency, locale)}
                </Text>
              </View>
            </View>
          </>
        )}
      </View>
    );
  };

  // Render footer based on document type
  const renderBudgetCoverageSection = (): React.ReactNode => {
    if (type !== DocumentType.BUDGET || !pec?.organizationName) {
      return null;
    }

    return (
      <View style={{ marginTop: 8, marginBottom: 6 }}>
        <Text style={typographyStyles.body2}>
          {t('hospital.billing.pdf.budget.attention')} {pec.organizationName}
        </Text>
        {totalBill > 0 && (
          <>
            <Text style={typographyStyles.body2}>
              {t('hospital.billing.pdf.budget.total_rating')} {formatCurrency(totalBill, currency, locale)}
            </Text>
            <Text style={typographyStyles.body2}>
              {t('hospital.billing.pdf.budget.coverage')}
            </Text>
            <View style={{ borderBottomWidth: 1, borderBottomColor: '#000', marginVertical: 6, width: '90%' }} />
          </>
        )}
      </View>
    );
  };

  const renderFooter = (): React.ReactNode => {
    // Only render for specific investigation UUID (line 257 from BillPDF.tsx)
    // if (uuidInvestigation !== 'cd54d9d8-23af-439b-af94-616fd8e24308') {
    //   return null;
    // }

    switch (type) {
      case DocumentType.BUDGET:
        return (
          <View wrap={false} style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 12 }}>
            <View style={{ width: '45%' }}>
              <Image src={SignatureFinancier} style={{ width: '100%', height: 'auto' }} />
            </View>
            <View style={{ width: '45%' }}>
              <Image src={SignatureMajor} style={{ width: '100%', height: 'auto' }} />
            </View>
          </View>
        );
      
      case DocumentType.SUMMARY:
        return (
          <View wrap={false} style={{ flexDirection: 'row', justifyContent: 'flex-start', marginTop: layout.footer.marginTop }}>
            <View style={{ width: layout.footer.signatureWidth }}>
              <Image src={SignatureMajor} style={{ width: '100%', height: 'auto' }} />
            </View>
          </View>
        );
      
      case DocumentType.INVOICE:
        if (uuidInvestigation === 'cd54d9d8-23af-439b-af94-616fd8e24308') {
        return (
          <>

            <View wrap={false} style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 25, alignItems: 'flex-start' }}>
              <View style={{ width: '45%', alignItems: 'center' }}>
                <Text style={[typographyStyles.body2, { marginBottom: 3, textAlign: 'center', fontWeight: 'bold', height: 20 }]}>
                  {t('pages.hospital.billing.pdf.financial_manager')}
                </Text>
                <Image src={SignatureFinancier} style={{ width: '70%', height: 'auto' }} />
              </View>
              <View style={{ width: '45%', alignItems: 'center' }}>
                <Text style={[typographyStyles.body2, { marginBottom: 3, textAlign: 'center', fontWeight: 'bold', height: 20 }]}>
                  {t('hospital.billing.pdf.director')}
                </Text>
                <Image src={SignatureDirecteur} style={{ width: '70%', height: 'auto' }} />
              </View>
            </View>
          </>
        );
      }
      return null;
      default:
        return null;
    }
  };

  return (
    <PDFDocument
      title={t(`hospital.billing.pdf.title.${documentTypeToString(type)}`)}
      patientInfo={renderPatientAndBillInfo()}
      config={config}
      locale={locale}
      pageLayout={layout.page}
      headerLayout={layout.header}
      patientInfoMarginBottom={layout.patientInfoMarginBottom}
      footer={renderFooter()}
      metadataFooter={{
        patient,
        documentReference: String(bill.documentNumber),
        documentDate: bill.createdAt,
        validatedBy: prescribingDoctor
          ? {
              name: formatResearcherName(prescribingDoctor),
              title: t('hospital.billing.prescribingDoctor'),
            }
          : undefined,
        locale,
        hospitalName: config.hospitalName,
        city: config.city,
      }}
    >
      {/* Status banner (SHER-416) */}
      {renderStatusBanner()}

      {/* Subtitle */}
      {renderSubtitle()}
      
      {/* Prelude content */}
      {renderPrelude()}
      
      {/* Bill Form Title */}
      <Text style={[
        billFormTitleStyle,
        { fontWeight: 'bold', marginTop: layout.billFormTitle.marginTop },
      ]}>
        {t(`hospital.billing.pdf.billform_title.${documentTypeToString(type)}`)}
      </Text>
      
      {/* Bill Items Table */}
      <PDFBillItemsTable
        items={bill.billItems}
        currency={currency}
        locale={locale}
        tableLayout={layout.table}
      />

      {/* Tax Summary - only shown when tax is present */}
      {(bill.totalTax != null && bill.totalTax > 0) && (
        <View style={{ marginTop: 5, marginBottom: 5 }}>
          <View style={{ borderTopWidth: 1, borderTopColor: '#000', paddingTop: 4 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2 }}>
              <Text style={[typographyStyles.body2, { fontWeight: 'bold' }]}>
                {t('hospital.billing.bill.total_cost')}
              </Text>
              <Text style={typographyStyles.body2}>
                {formatCurrency(bill.totalGross - bill.totalDiscount, currency, locale)}
              </Text>
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2 }}>
              <Text style={[typographyStyles.body2, { fontWeight: 'bold' }]}>
                {t('hospital.billing.bill.total_tax')}
              </Text>
              <Text style={typographyStyles.body2}>
                {formatCurrency(bill.totalTax, currency, locale)}
              </Text>
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2, borderTopWidth: 1, borderTopColor: '#000' }}>
              <Text style={[typographyStyles.body2, { fontWeight: 'bold', fontSize: 11 }]}>
                {t('hospital.billing.bill.total_net')}
              </Text>
              <Text style={[typographyStyles.body2, { fontWeight: 'bold', fontSize: 11 }]}>
                {formatCurrency(bill.totalNet || 0, currency, locale)}
              </Text>
            </View>
          </View>
        </View>
      )}

      {/* Payment Summary */}
      {renderPaymentSummaryTable()}

      {/* Devis insurance fill-in — main flow so it stays on page 1 with short tables (SHER-430) */}
      {renderBudgetCoverageSection()}
    </PDFDocument>
  );
};

export default BillPDFDocument;

